import { useEffect, useId, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useGetMe } from '../../api/generated/identity/identity';
import { useListOccurrenceAttendance } from '../../api/generated/attendance/attendance';
import {
  getListOccurrencesQueryKey,
  listOccurrences,
} from '../../api/generated/scheduling/scheduling';
import type { ClassOccurrence, ListOccurrencesParams, Me } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { formatDateInPersonTimezone, formatTimeInPersonTimezone } from '../../i18n/datetime';
import { attendanceWindow } from './attendanceWindow';
import { type ListState, listState } from './listState';
import styles from './attendance.module.css';

export function AttendancePage() {
  const meQuery = useGetMe();
  const me = meQuery.data;

  // A janela nao existe antes de `me`, porque ela e recortada no fuso da
  // pessoa. Quebrar a tela em duas evita o fuso provisorio da primeira
  // renderizacao — que seria o do navegador de novo, que e o defeito.
  if (!me) return null;
  return <CompletedClasses me={me} />;
}

function CompletedClasses({ me }: { readonly me: Me }) {
  const { t } = useTranslation();
  const range = useMemo(() => attendanceWindow(new Date(), me.timezone), [me.timezone]);
  // `status` vai para o SERVIDOR, e nao so para o filtro daqui: o LIMIT do
  // servidor recai sobre o recorte pedido, entao pedir a janela inteira e
  // descartar as agendadas na tela gastava a pagina com aulas que esta tela
  // nunca mostra.
  const params = useMemo<ListOccurrencesParams>(
    () => ({ ...range, status: 'completed' }),
    [range],
  );

  /**
   * A janela inteira, e nao a primeira pagina dela.
   *
   * `listOccurrences` devolve ASCENDENTE com LIMIT (`scheduling/service.go`: teto
   * duro de 500) e esta tela ordena do mais recente para o mais antigo. As duas
   * coisas juntas produziam o pior desfecho possivel: a pagina lida comecava no
   * inicio do mes anterior, e a aula que acabou de terminar — que e por que
   * alguem abre esta tela — ficava atras do cursor, invisivel e sem aviso.
   * Seguir o cursor ate `nextCursor` nulo e o unico jeito de a ultima aula da
   * janela estar na lista, porque o contrato nao oferece leitura decrescente.
   */
  const occQuery = useInfiniteQuery({
    queryKey: getListOccurrencesQueryKey(params),
    queryFn: ({ pageParam, signal }) =>
      listOccurrences(pageParam === undefined ? params : { ...params, cursor: pageParam }, {
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = occQuery;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada, e nao e detalhe:
    // sem ele, a pagina que falha no meio da janela deixa `hasNextPage`
    // verdadeiro e `isFetchingNextPage` de volta em falso, o efeito dispara de
    // novo e a tela entra em tempestade de requisicoes contra o servidor que
    // acabou de recusar — numa janela de tres meses, indefinidamente.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  const completed = useMemo(() => {
    const pages = occQuery.data?.pages;
    // Enquanto ha cursor por seguir a janela ainda esta incompleta, e uma lista
    // parcial aqui nao e "menos dados": e a aula errada aberta sozinha logo
    // abaixo, com a leitura de presenca dela ja disparada.
    if (pages === undefined || hasNextPage) return undefined;
    return pages
      .flatMap((page) => page.items)
      .filter((occ) => occ.status === 'completed')
      // Mais recente primeiro: quem abre esta tela vem conferir a aula que
      // acabou de acontecer, e e ela que abre sozinha logo abaixo.
      .sort((a, b) => Date.parse(b.startsAtUtc) - Date.parse(a.startsAtUtc));
  }, [occQuery.data, hasNextPage]);

  const state = listState<ClassOccurrence>({
    isPending: occQuery.isPending,
    // A pagina que falha NO MEIO da janela e uma falha da leitura, e nao um
    // "ainda carregando" permanente — `completed` fica `undefined` enquanto
    // `hasNextPage` for verdadeiro, e numa janela que nunca vai fechar isso e
    // para sempre.
    //
    // A parcela `|| isFetchNextPageError` NAO acrescenta caso algum, e o
    // comentario que estava aqui afirmava o contrario ("`isError` so fala da
    // primeira pagina"): `isFetchNextPageError` e derivado como `isError &&
    // direcao === 'forward'` (`query-core/infiniteQueryObserver.js`), logo ele
    // implica `isError`. Fica escrito porque promessa de guarda inexistente e o
    // que faz o proximo leitor parar de procurar — a condicao de parada que
    // realmente segura o laco e a do efeito, acima, e nao esta linha.
    isError: occQuery.isError || isFetchNextPageError,
    error: occQuery.error,
    data: completed,
  });

  return (
    <div className={styles.page}>
      <h1>{t('attendance.title')}</h1>
      <ListFallback
        state={state}
        labels={{
          // Vazio desta lista e "nenhuma aula concluida", e nao "nenhum
          // registro de presenca": a de dentro fala das pessoas de uma aula, e
          // esta fala das aulas da janela.
          empty: t('attendance.noCompletedClasses'),
          // A recusa aqui e sobre a agenda inteira, e nao sobre uma aula: dizer
          // "a presenca desta aula" seria nomear uma aula que a tela nem chegou
          // a listar.
          refused: t('error.loadFailed'),
          refusedDetail: null,
        }}
        onRetry={() => void occQuery.refetch()}
      />
      {state.kind === 'loaded' ? (
        <ul className={styles.list}>
          {state.items.map((occ, index) => (
            <li key={occ.id}>
              <OccurrenceAttendance occurrence={occ} me={me} openByDefault={index === 0} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Uma aula concluida, com a presenca dela atras de um botao de abrir.
 *
 * A leitura da presenca e uma chamada **por ocorrencia** — e o contrato nao tem
 * rota que traga varias de uma vez. Disparar todas ao abrir a tela dava dezenas
 * de requisicoes paralelas para quem da aula em tempo integral numa janela de
 * tres meses. Enquanto o bloco esta fechado o componente que consulta nem
 * existe, entao a tela nasce com **uma** chamada: a da aula mais recente, que
 * abre sozinha para a pagina nao nascer muda.
 */
function OccurrenceAttendance({
  occurrence,
  me,
  openByDefault,
}: {
  readonly occurrence: ClassOccurrence;
  readonly me: Me;
  readonly openByDefault: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(openByDefault);
  const panelId = useId();

  // Data, horario e tipo. So a data nao bastava: duas aulas do mesmo dia
  // apareciam como dois rotulos identicos, e a tela do professor Bruno era
  // literalmente duas datas soltas.
  const label = [
    formatDateInPersonTimezone(occurrence.startsAtUtc, me.timezone, me.locale),
    formatTimeInPersonTimezone(occurrence.startsAtUtc, me.timezone, me.locale),
    t(`schedule.kind.${occurrence.kind}`),
  ].join(' · ');

  return (
    <div className={styles.block}>
      <h2 className={styles.blockTitle}>
        <button
          type="button"
          className={styles.disclosure}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((current) => !current)}
        >
          {label}
        </button>
      </h2>
      {/*
        Fora do painel: o ajuste e do Super Admin e nao depende de abrir a
        presenca. Dentro, ele sumiria junto com o bloco fechado.
      */}
      {me.role === 'super_admin' ? (
        <Link to={`/attendance/${occurrence.id}/adjust`}>{t('attendance.adjustTitle')}</Link>
      ) : null}
      <div id={panelId}>{open ? <OccurrenceRecords occurrenceId={occurrence.id} /> : null}</div>
    </div>
  );
}

function OccurrenceRecords({ occurrenceId }: { readonly occurrenceId: string }) {
  const { t } = useTranslation();
  const query = useListOccurrenceAttendance(occurrenceId);
  const state = listState({
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    data: query.data,
  });

  if (state.kind !== 'loaded') {
    return (
      <ListFallback
        state={state}
        labels={{
          empty: t('attendance.empty'),
          // 403 e 404 desta leitura sao decisao sobre **quem pergunta**. O
          // texto generico de falha mandava a aluna recusada procurar defeito
          // de rede onde havia uma regra de acesso, e o detalhe diz o unico
          // caminho que resolve — a escola, e nao o botao.
          refused: t('attendance.refused'),
          refusedDetail: t('attendance.refusedDetail'),
        }}
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <ul>
      {state.items.map((rec) => (
        <li key={rec.id}>
          {rec.person.displayName}: {t(`attendance.status.${rec.computed}`)}
          {rec.adjustment
            ? ` / ${t(`attendance.status.${rec.adjustment.status}`)}`
            : ` (${t('attendance.noAdjustment')})`}
        </li>
      ))}
    </ul>
  );
}

/**
 * Os textos que dependem de **qual** lista falhou.
 *
 * Vem juntos, e nao um `emptyLabel` solto, porque as duas listas desta tela
 * falam de coisas diferentes: a de fora, das aulas da janela; a de dentro, das
 * pessoas de uma aula. Com so o vazio parametrizado, a recusa caia num texto
 * generico de falha para as duas — e "nao foi possivel carregar" e uma frase
 * sobre a rede dita a quem, na verdade, nao tem acesso. Exigir os tres de uma
 * vez torna inexprimivel a chamada que escolhe o vazio certo e deixa a recusa
 * no texto de outra pessoa.
 */
interface FallbackLabels {
  readonly empty: string;
  readonly refused: string;
  /** @nullable Nulo quando a recusa nao tem segunda linha a acrescentar. */
  readonly refusedDetail: string | null;
}

/**
 * Tudo o que a lista pode ser quando nao e lista. O `switch` sem `default` e o
 * que garante que um estado novo no tipo quebre a compilacao aqui em vez de
 * cair calado num ramo vazio.
 */
function ListFallback({
  state,
  labels,
  onRetry,
}: {
  readonly state: ListState<unknown>;
  readonly labels: FallbackLabels;
  readonly onRetry: () => void;
}) {
  const { t } = useTranslation();

  switch (state.kind) {
    case 'loading':
      return (
        <p className={styles.pending} role="status">
          {t('app.loading')}
        </p>
      );
    case 'refused':
      // Sem "tentar de novo": a recusa e sobre quem pergunta, e repetir devolve
      // a mesma resposta. Botao inutil aqui e so mais uma promessa falsa — o
      // que substitui o botao e o detalhe, que diz o caminho que existe.
      return (
        <Alert tone="error">
          {labels.refused}
          {labels.refusedDetail === null ? null : <p>{labels.refusedDetail}</p>}
        </Alert>
      );
    case 'failed':
      return (
        <Alert tone="error">
          {t('error.loadFailed')}
          <Button onClick={onRetry}>{t('action.retry')}</Button>
        </Alert>
      );
    case 'empty':
      return <p>{labels.empty}</p>;
    case 'loaded':
      return null;
  }
}
